import { classNames } from "primereact/utils";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../../../stateManagement/hooks/redux";
import { calculatePercent } from "../../../../../features/dataFormatFunctions/dataFormatter";
import { roadBedFiltersOptions } from "../roadBedLayerSegmentsOverlay";
import PathNormativitiesMapFilters from "./PathNormativitiesMapFilters";
import { useFetchNormativityAggregateReportMutation } from "../../../../../api/rtk/base/slices/diagnosticsApiSlice";

const pathNormativitiesInfoInitialState = {
    total: '0,00',
    normative: '0,00',
    notNormative: '0,00',
    normativePercent: '0,0',
    notNormativePercent: '0,0'
}

export default function PathNormativitiesMeasureTypeOverlay({ measureType }: any) {
    const [pathNormativitiesStatisticsLoading, setPathNormativitiesStatisticsLoading] = useState<boolean>(true)
    const [pathNormativitiesInfo, setPathNormativitiesInfo] = useState<any>();
    const [normativityAggregateReportData, setNormativityAggregateReportData] = useState<any>(null)

    let pathNormativitiesMapFilters = useAppSelector(state => state.diagnosticsPathNormativities.pathNormativitiesMapFilters)
    let pathNormativitiesYearMap = useAppSelector(state => state.diagnosticsPathNormativities.pathNormativitiesMapYearFilter)
    let pathNormativitiesMapFiltersAreExpanded = useAppSelector(state => state.diagnosticsPathNormativities.pathNormativitiesMapFiltersAreExpanded)

    const [fetchNormativityAggregateReport, { isError: normativityAggregateReportIsError }] = useFetchNormativityAggregateReportMutation()

    useEffect(() => {
        fetchPathNormativityStatistics()
    }, [pathNormativitiesMapFilters, pathNormativitiesYearMap, location])

    useEffect(() => {
        if (normativityAggregateReportData) {
            const prefix = measureType === "Total" ? '' : measureType
            const total = normativityAggregateReportData?.[`${prefix}Total`]
            const normative = normativityAggregateReportData?.[`${prefix}Normative`]
            const notNormative = normativityAggregateReportData?.[`${prefix}NotNormative`]
            const formatNumber = (num: number, decimals: number = 2): string => num?.toFixed(decimals).replace('.', ',');
            const totalFormatted = formatNumber(total);
            const normativeFormatted = formatNumber(normative);
            const notNormativeFormatted = formatNumber(notNormative);
            const normativePercent = formatNumber(calculatePercent(total, normative), 1);
            const notNormativePercent = formatNumber(calculatePercent(total, notNormative), 1);
            setPathNormativitiesInfo({
                total: totalFormatted,
                normative: normativeFormatted,
                notNormative: notNormativeFormatted,
                normativePercent,
                notNormativePercent
            })
        } else {
            setPathNormativitiesInfo(pathNormativitiesInfoInitialState)
        }
    }, [normativityAggregateReportData, measureType])

    async function fetchPathNormativityStatistics() {
        const { roadBedTypes, raions, isOnHighwayNetworkSegment } = pathNormativitiesMapFilters ?? {}
        setPathNormativitiesStatisticsLoading(true)
        // roadBedTypes filter
        let roadBedTypesRequestBodyArray = []
        let anyRoadBedFilter = Object.entries(roadBedTypes).filter(([key, value]) => value === true).map(([key, value]) => key)
        if (anyRoadBedFilter.length > 0) {
            let roadBedFiltersTempArrayActive = Object.entries(roadBedTypes).filter(([key, value]) => (value === true && key !== 'noRoadBedType' && key !== 'ignoreRoadBedTypes')).map(([key, value]) => key)
            let roadBedFiltersArray = roadBedFiltersTempArrayActive.map(key => key === 'noRoadBedType' ? null : roadBedFiltersOptions[key].roadbedTypeId).filter(item => item != null)
            if (roadBedFiltersArray.length > 0) {
                roadBedTypesRequestBodyArray.push(...roadBedFiltersArray)
            }
            if (roadBedTypes.noRoadBedType) {
                roadBedTypesRequestBodyArray.push(-1)
            }
            if (roadBedTypes.ignoreRoadBedTypes) {
                roadBedTypesRequestBodyArray = []
            }
        }

        // raions filter
        let raionsRequestBodyArray = []
        let anyRaionFilter = Object.entries(raions).filter(([key, value]) => (value === true)).map(([key, value]) => key)
        if (anyRaionFilter.length > 0) {
            let raionsFiltersArray = Object.entries(raions).filter(([key, value]) => (value === true && key !== 'noRaion')).map(([key, value]) => parseFloat(key))
            if (raionsFiltersArray.length > 0) {
                raionsRequestBodyArray.push(...raionsFiltersArray)
            }
            if (raions.noRaion) {
                raionsRequestBodyArray.push(-1)
            }
            if (raions.ignoreRaions) {
                raionsRequestBodyArray = []
            }
        }
        if (anyRoadBedFilter.length > 0 && anyRaionFilter.length > 0) {
            const normativityAggregateReport: any = await fetchNormativityAggregateReport({
                Year: parseFloat(pathNormativitiesYearMap),
                HighwayNetworks: isOnHighwayNetworkSegment ? 1 : -1,
                PavementTypes: roadBedTypesRequestBodyArray,
                Raions: raionsRequestBodyArray,
                Categories: []
            })
            if (normativityAggregateReport?.data) {
                setNormativityAggregateReportData(normativityAggregateReport?.data)
            } else {
                setNormativityAggregateReportData(null)
            }
            setPathNormativitiesStatisticsLoading(false)
        } else {
            setNormativityAggregateReportData(null)
        }
        setPathNormativitiesStatisticsLoading(false)
    }
    // - Класс всего окна информации для карты участков нормативности
    let mapOverlayPathNormativitiesClassName = classNames({
        "map-overlay-path-normativities": true,
        "map-overlay": true,
        "filters-div-opened": pathNormativitiesMapFiltersAreExpanded
    })

    return (
        <>
            <div className={mapOverlayPathNormativitiesClassName}>
                {pathNormativitiesStatisticsLoading
                    ?
                    <div className="map-overlay-info-block-loading">
                        <div>
                            <span className="spinner-border spinner-border-sm me-2 ms-2" role="status" aria-hidden="true"></span>
                            <span>Загрузка...</span>
                        </div>
                    </div>
                    :
                    <>
                        {normativityAggregateReportIsError || !normativityAggregateReportData
                            ?
                            <div className="map-overlay-info-block-loading">
                                <div>
                                    <span>Ошибка загрузки.</span>
                                </div>
                            </div>
                            :
                            <div className="map-overlay-info-block">
                                <div className="field-col">
                                    <span className="info-hint-label-field">Обследовано дорог:</span>
                                    <span className="info-hint-label-field">Из них в нормативе:</span>
                                    <span className="info-hint-label-field">Не в нормативе:</span>
                                </div>
                                <div className="value-col">
                                    {/* нужен запрос на общую протяженность опорной сети */}
                                    <span className="info-hint-label-value">{pathNormativitiesInfo?.total} км</span>
                                    <span className="info-hint-label-value">{pathNormativitiesInfo?.normativePercent}% {pathNormativitiesInfo?.normative} км</span>
                                    <span className="info-hint-label-value">{pathNormativitiesInfo?.notNormativePercent}% {pathNormativitiesInfo?.notNormative} км</span>
                                </div>
                            </div>
                        }
                    </>
                }
                <PathNormativitiesMapFilters />
            </div>
        </>
    )
}
